import { movies } from "@/mocks/movies";
import { tvSeries } from "@/mocks/tv";
import type { FilmTitle, SortKey } from "@/types/film";

export const allTitles: FilmTitle[] = [...movies, ...tvSeries];

export const PAGE_SIZE = 8;

export interface YearRangeOption {
  id: string;
  label: string;
  min: number;
  max: number;
}

export const YEAR_RANGES: YearRangeOption[] = [
  { id: "2020s", label: "2020 年至今", min: 2020, max: 2999 },
  { id: "2010s", label: "2010 - 2019", min: 2010, max: 2019 },
  { id: "2000s", label: "2000 - 2009", min: 2000, max: 2009 },
  { id: "1990s", label: "1990 - 1999", min: 1990, max: 1999 },
  { id: "earlier", label: "1990 年以前", min: 1900, max: 1989 },
];

export const GENRE_OPTIONS = [
  "剧情",
  "动作",
  "科幻",
  "悬疑",
  "犯罪",
  "爱情",
  "喜剧",
  "动画",
  "奇幻",
  "战争",
  "历史",
  "音乐",
  "传记",
  "冒险",
  "惊悚",
  "西部",
  "家庭",
  "青春",
  "医疗",
  "金融",
  "政治",
];

export const REGION_OPTIONS = ["美国", "英国", "法国", "新西兰"];

export const SORT_OPTIONS: { id: SortKey; label: string }[] = [
  { id: "popularity", label: "按热度" },
  { id: "year", label: "按上映时间" },
  { id: "rating", label: "按评分" },
];

export function getTitleById(id: string): FilmTitle | undefined {
  return allTitles.find((item) => item.id === id);
}

export function getFeatured(type: "movie" | "tv", limit = 8): FilmTitle[] {
  return allTitles
    .filter((item) => item.type === type)
    .sort((a, b) => b.popularity - a.popularity)
    .slice(0, limit);
}

export interface FilterParams {
  type: "all" | "movie" | "tv";
  genres: string[];
  regions: string[];
  yearRanges: string[];
  topicTag: string;
  keyword: string;
}

export function filterTitles(params: FilterParams): FilmTitle[] {
  const { type, genres, regions, yearRanges, topicTag, keyword } = params;
  const kw = keyword.trim().toLowerCase();
  const ranges = YEAR_RANGES.filter((r) => yearRanges.includes(r.id));

  return allTitles.filter((item) => {
    if (type !== "all" && item.type !== type) return false;

    if (genres.length > 0 && !item.genres.some((g) => genres.includes(g))) return false;

    if (regions.length > 0 && !regions.includes(item.region)) return false;

    if (ranges.length > 0 && !ranges.some((r) => item.year >= r.min && item.year <= r.max)) {
      return false;
    }

    if (topicTag && !item.tags.includes(topicTag)) return false;

    if (kw) {
      const haystack = [
        item.title,
        item.originalTitle,
        item.director,
        item.region,
        ...item.genres,
        ...item.cast,
        ...item.tags,
      ]
        .join(" ")
        .toLowerCase();
      if (!haystack.includes(kw)) return false;
    }

    return true;
  });
}

export function sortTitles(list: FilmTitle[], sort: SortKey): FilmTitle[] {
  const copied = [...list];
  if (sort === "year") {
    copied.sort((a, b) => b.year - a.year || b.rating - a.rating);
  } else if (sort === "rating") {
    copied.sort((a, b) => b.rating - a.rating || b.year - a.year);
  } else {
    copied.sort((a, b) => b.popularity - a.popularity || b.rating - a.rating);
  }
  return copied;
}

export function paginate(list: FilmTitle[], page: number, pageSize = PAGE_SIZE) {
  const totalPages = Math.max(1, Math.ceil(list.length / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = (safePage - 1) * pageSize;
  return {
    items: list.slice(start, start + pageSize),
    totalPages,
    page: safePage,
    total: list.length,
  };
}

export function searchTitles(
  keyword: string,
  options?: { type?: "all" | "movie" | "tv"; sort?: SortKey }
): FilmTitle[] {
  const list = filterTitles({
    type: options?.type ?? "all",
    genres: [],
    regions: [],
    yearRanges: [],
    topicTag: "",
    keyword,
  });
  return options?.sort ? sortTitles(list, options.sort) : sortTitles(list, "popularity");
}

export function getRelated(title: FilmTitle, limit = 6): FilmTitle[] {
  return allTitles
    .filter((item) => item.id !== title.id)
    .map((item) => {
      let score = 0;
      score += item.genres.filter((g) => title.genres.includes(g)).length * 3;
      score += item.tags.filter((t) => title.tags.includes(t)).length * 2;
      if (item.type === title.type) score += 2;
      if (Math.abs(item.year - title.year) <= 6) score += 1;
      return { item, score };
    })
    .sort((a, b) => b.score - a.score || b.item.rating - a.item.rating)
    .slice(0, limit)
    .map((entry) => entry.item);
}

export function getHotTitles(limit = 6): FilmTitle[] {
  return [...allTitles].sort((a, b) => b.popularity - a.popularity).slice(0, limit);
}